import { uniqueId } from 'lodash';

import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { addMockImage } from '@common/utils';
import { createSelector } from '@reduxjs/toolkit';

export const hasItems =
    (applianceSource: ApplianceSource) =>
    (cookingAppliances: ApplianceModel[]): boolean =>
        cookingAppliances.some((cookingAppliance) => cookingAppliance.productSource === applianceSource);

// various show/hide options determined by availability of different offerings
export const hasProgramItems = hasItems(ApplianceSource.PROGRAM);
export const hasInStoreItems = hasItems(ApplianceSource.IN_STORE);

export const isApplianceSource =
    (applianceSource: ApplianceSource) =>
    (cookingAppliance: ApplianceModel): boolean =>
        cookingAppliance.productSource === applianceSource;

export const isApplianceSourceProgram = isApplianceSource(ApplianceSource.PROGRAM);
export const isApplianceSourceInStore = isApplianceSource(ApplianceSource.IN_STORE);

// TODO: Consolidate with sinks implemention
type SelectorCallback<T> = (
    data: ApplianceModel[],
    productSource: ApplianceSource,
    applianceType?: ApplianceType,
    shouldAddProductSourceToFeatures?: boolean
) => T;

// TODO: Consolidate with sinks implemention
export const makeCookingAppliancesSelector = () =>
    createSelector<
        [
            SelectorCallback<ApplianceModel[]>,
            SelectorCallback<ApplianceSource>,
            SelectorCallback<ApplianceType | undefined>,
            SelectorCallback<boolean | undefined>
        ],
        ApplianceModel[]
    >(
        [
            (data) => data,
            (_, productSource) => productSource,
            (_, __, applianceType) => applianceType,
            (_, __, ___, shouldAddProductSourceToFeatures) => shouldAddProductSourceToFeatures,
        ],
        (data, productSource, applianceType, shouldAddProductSourceToFeatures = false) => {
            return data
                ?.map((cookingAppliance) => ({
                    ...cookingAppliance,
                    uuid: cookingAppliance.uuid ?? uniqueId(),
                    productSource,
                    applianceType: cookingAppliance.applianceType ?? applianceType,
                    features: shouldAddProductSourceToFeatures
                        ? [
                              ...(cookingAppliance.features ?? []),
                              {
                                  name: 'cookingApplianceType',
                                  description: 'Type',
                                  position: 10,
                                  value: cookingAppliance.type,
                              },
                              {
                                  name: 'productSource',
                                  description: 'Product Source',
                                  position: 11,
                                  value: productSource,
                              },
                          ]
                        : cookingAppliance.features ?? [], //TODO: cookingAppliance.features in data may actually be undefined despite current typing saying otherwise. Fixing types required
                }))
                ?.map(addMockImage);
        }
    );
